import { useMemo } from 'react';
import type { DigitalCardFace } from 'src/components/_common/DigitalCard/context';

interface UseFlipActionsProps {
  faces: DigitalCardFace[];
  currentFaceIdx: number;
  setCurrentFaceIdx: (value: number | ((prev: number) => number)) => void;
  isEnabled: boolean;
}

/**
 * 카드 뒤집기 기능을 제공하는 훅
 *
 * @description
 * 카드의 면 전환 로직을 처리하고 현재/이전/다음 면의 상태를 관리합니다.
 *
 * @param props
 * ```ts
 * {
 *   faces: DigitalCardFace[],     // 카드의 모든 면 배열
 *   currentFaceIdx: number,       // 현재 표시 중인 면의 인덱스
 *   setCurrentFaceIdx: (idx: number) => void,  // 현재 면 변경 함수
 *   isEnabled: boolean            // 플립 기능 활성화 여부
 * }
 * ```
 *
 * @returns
 * ```ts
 * {
 *   visibleFaces: Array<{
 *     face: DigitalCardFace,
 *     position: 'prev' | 'current' | 'next'
 *   }>,                          // 화면에 표시되는 면들
 *   handleFlipToNext: () => void,  // 다음 면으로 전환
 *   handleFlipToPrev: () => void   // 이전 면으로 전환
 * }
 * ```
 */
const useFlipActions = ({
  faces: originalFaces,
  currentFaceIdx,
  setCurrentFaceIdx,
  isEnabled,
}: UseFlipActionsProps) => {
  /**
   * 카드 면 처리 로직
   * flip을 위해서는 최소 3개의 면이 필요함. (좌/우로 flip될 수 있기 때문)
   * originalsFaces가 1개인 경우는 3개의 면이 필요.
   * originalsFaces가 2개인 경우는 4개의 면이 필요. (2개를 3개인 것처럼 보여줄 수 없으므로 짝수인 4개)
   */
  const faces = useMemo(() => {
    let processedFaces = [...originalFaces];

    // 1개일 때: 0번째 인덱스를 복제하여 2개 추가
    if (processedFaces.length === 1) {
      processedFaces = [
        { ...processedFaces[0], idx: processedFaces[0].idx },
        { ...processedFaces[0], idx: processedFaces[0].idx + 1 },
        { ...processedFaces[0], idx: processedFaces[0].idx + 2 },
      ];
    } else if (processedFaces.length === 2) {
      // 2개일 때: 0번째 인덱스를 복제하여 1개 추가
      processedFaces = [
        { ...processedFaces[0], idx: processedFaces[0].idx },
        { ...processedFaces[1], idx: processedFaces[1].idx },
        { ...processedFaces[0], idx: processedFaces[0].idx + 2 },
        { ...processedFaces[1], idx: processedFaces[1].idx + 2 },
      ];
    }

    return processedFaces;
  }, [originalFaces]);

  /**
   * 보이는 카드 면 계산 로직
   */
  const visibleFaces = useMemo(() => {
    // 플립 기능이 비활성화되면 현재 면만 보여줌
    if (!isEnabled) return [{ face: faces[currentFaceIdx], position: 'current' as const }];

    const prevFaceIdx = currentFaceIdx === 0 ? faces.length - 1 : currentFaceIdx - 1;
    const nextFaceIdx = currentFaceIdx === faces.length - 1 ? 0 : currentFaceIdx + 1;

    return [
      { face: faces[prevFaceIdx], position: 'prev' as const },
      { face: faces[currentFaceIdx], position: 'current' as const },
      { face: faces[nextFaceIdx], position: 'next' as const },
    ];
  }, [faces, currentFaceIdx, isEnabled]);

  /**
   * 카드 뒤집기 액션들
   */
  const actions = useMemo(
    () => ({
      // 1. 이전 면으로 넘어가는 액션
      handleFlipToPrev: () => {
        if (!isEnabled) return;
        setCurrentFaceIdx((prev) => (prev === 0 ? faces.length - 1 : prev - 1));
      },
      // 2. 다음 면으로 넘어가는 액션
      handleFlipToNext: () => {
        if (!isEnabled) return;
        setCurrentFaceIdx((prev) => (prev === faces.length - 1 ? 0 : prev + 1));
      },
    }),
    [isEnabled, faces.length, setCurrentFaceIdx],
  );

  return {
    visibleFaces,
    ...actions,
  };
};

export default useFlipActions;
